import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { useWindowSize } from '../../hooks';
import { isValidDateBR, formatDateBR, convertDateBRToISO } from '../../utils/dateUtils';

/**
 * Componente de interface de pesquisa de agendamentos
 * Fornece controles de filtro e busca para agendamentos
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Function} props.onSearch - Callback quando filtros são alterados
 * @param {string} props.defaultDate - Data padrão para o filtro (formato ISO)
 * @param {Array} props.professionals - Lista de profissionais para filtro
 * @param {Array} props.statusOptions - Opções de status para filtro
 * @returns {JSX.Element} - Componente React
 */
const AppointmentSearch = ({ 
  onSearch, 
  defaultDate = null,
  professionals = [],
  statusOptions = [
    { value: 'all', label: 'Todos os status' },
    { value: 'pending', label: 'Pendentes' },
    { value: 'completed', label: 'Confirmados' },
    { value: 'canceled', label: 'Cancelados' }
  ]
}) => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  
  // Estados para os filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(defaultDate || '');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('all');
  const [professionalId, setProfessionalId] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(!isMobile);
  
  // Estados para exibição de datas no formato brasileiro
  const [startDateBR, setStartDateBR] = useState('');
  const [endDateBR, setEndDateBR] = useState('');
  
  // Estado para erros de validação de data
  const [dateErrors, setDateErrors] = useState({ start: '', end: '' });
  
  // Inicializar datas no formato brasileiro com base no defaultDate
  useEffect(() => {
    if (defaultDate) {
      setStartDateBR(formatDateBR(defaultDate));
    }
  }, [defaultDate]);
  
  // Aplicar os filtros usando o callback
  const applyFilters = () => {
    if (onSearch) {
      onSearch({
        searchTerm,
        startDate,
        endDate,
        status,
        professionalId: professionalId || null
      });
    }
  };
  
  // Debounce para aplicar filtros só após o usuário parar de digitar/modificar
  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters();
    }, 500); // Esperar 500ms após a última mudança
    
    return () => clearTimeout(timer); // Limpar o timeout se houver nova mudança
  }, [searchTerm, startDate, endDate, status, professionalId]);

  // Limpar todos os filtros
  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setStartDateBR('');
    setEndDateBR('');
    setStatus('all');
    setProfessionalId('');
    setDateErrors({ start: '', end: '' });
  };
  
  // Formatter para entrada no formato brasileiro de data
  const formatBRDateInput = (value, setter) => {
    // Remove caracteres não numéricos, exceto "/"
    let cleaned = value.replace(/[^\d\/]/g, '');
    
    // Aplica a máscara DD/MM/YYYY
    if (cleaned.length > 0) {
      // Coloca "/" após dia e mês se ainda não existir
      if (cleaned.length > 2 && cleaned.charAt(2) !== '/') {
        cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
      }
      if (cleaned.length > 5 && cleaned.charAt(5) !== '/') {
        cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5);
      }
      
      // Limita ao tamanho máximo (10 caracteres: DD/MM/YYYY)
      if (cleaned.length > 10) {
        cleaned = cleaned.slice(0, 10);
      }
    }
    
    setter(cleaned);
  };
  
  // Validar e converter data ao sair do campo
  const handleBRDateBlur = (value, isoSetter, errorType) => {
    // Se estiver vazio, limpar tudo
    if (!value.trim()) {
      isoSetter('');
      setDateErrors(prev => ({ ...prev, [errorType]: '' }));
      return;
    }
    
    // Verificar se é uma data válida
    if (isValidDateBR(value)) {
      const isoDate = convertDateBRToISO(value);
      isoSetter(isoDate);
      setDateErrors(prev => ({ ...prev, [errorType]: '' }));
    } else {
      setDateErrors(prev => ({ ...prev, [errorType]: 'Data inválida' }));
    }
  };
  
  // Filtros aplicados atualmente (para exibir tags)
  const activeFilters = [
    searchTerm && { id: 'searchTerm', label: `Busca: ${searchTerm}`, onRemove: () => setSearchTerm('') },
    startDate && { id: 'startDate', label: `De: ${startDateBR}`, onRemove: () => { setStartDate(''); setStartDateBR(''); } },
    endDate && { id: 'endDate', label: `Até: ${endDateBR}`, onRemove: () => { setEndDate(''); setEndDateBR(''); } },
    status !== 'all' && { id: 'status', label: `Status: ${statusOptions.find(s => s.value === status)?.label || status}`, onRemove: () => setStatus('all') },
    professionalId && { 
      id: 'professional', 
      label: `Profissional: ${professionals.find(p => p.id === professionalId)?.nome || 'Selecionado'}`, 
      onRemove: () => setProfessionalId('') 
    }
  ].filter(Boolean);

  return (
    <div className="appointment-search bg-white rounded-lg shadow">
      {/* Barra de busca principal sempre visível */}
      <div className="p-4 border-b">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar por nome, telefone ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-0 py-3 pl-10 pr-20 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          />
          <div className="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="inline-flex items-center border border-gray-200 rounded px-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              Filtros
            </button>
          </div>
        </div>
      </div>
      
      {/* Filtros avançados (colapsáveis em mobile) */}
      {showAdvancedFilters && (
        <div className="p-4 border-b">
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 md:grid-cols-4 gap-4'}`}>
            {/* Filtro de data de início */}
            <div>
              <label htmlFor="startDateBR" className="block text-sm font-medium text-gray-700 mb-1">De</label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <CalendarIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input 
                  type="text" 
                  id="startDateBR"
                  placeholder="DD/MM/AAAA"
                  value={startDateBR}
                  onChange={(e) => formatBRDateInput(e.target.value, setStartDateBR)}
                  onBlur={() => handleBRDateBlur(startDateBR, setStartDate, 'start')}
                  className={`pl-9 w-full px-3 py-2 border ${dateErrors.start ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                />
                {dateErrors.start && (
                  <p className="mt-1 text-xs text-red-500">{dateErrors.start}</p>
                )}
              </div>
            </div>
            
            {/* Filtro de data final */}
            <div>
              <label htmlFor="endDateBR" className="block text-sm font-medium text-gray-700 mb-1">Até</label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <CalendarIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input 
                  type="text" 
                  id="endDateBR"
                  placeholder="DD/MM/AAAA"
                  value={endDateBR}
                  onChange={(e) => formatBRDateInput(e.target.value, setEndDateBR)}
                  onBlur={() => handleBRDateBlur(endDateBR, setEndDate, 'end')}
                  className={`pl-9 w-full px-3 py-2 border ${dateErrors.end ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                />
                {dateErrors.end && (
                  <p className="mt-1 text-xs text-red-500">{dateErrors.end}</p>
                )}
              </div>
            </div>
            
            {/* Filtro de status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Filtro de profissional */}
            <div>
              <label htmlFor="professional" className="block text-sm font-medium text-gray-700 mb-1">Profissional</label>
              <select
                id="professional"
                value={professionalId}
                onChange={(e) => setProfessionalId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos os profissionais</option>
                {professionals.map(professional => (
                  <option key={professional.id} value={professional.id}>
                    {professional.nome || professional.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Botão de limpar filtros */}
          {activeFilters.length > 0 && (
            <div className="mt-4">
              <button
                onClick={clearFilters}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                Limpar todos os filtros
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Tags de filtros ativos */}
      {activeFilters.length > 0 && (
        <div className="px-4 py-2 flex flex-wrap gap-2">
          {activeFilters.map(filter => (
            <span
              key={filter.id}
              className="inline-flex items-center bg-blue-50 text-blue-700 text-xs rounded px-2 py-1"
            >
              {filter.label}
              <button 
                onClick={filter.onRemove}
                className="ml-1 text-blue-500 hover:text-blue-800"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentSearch; 